import { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { ChevronDown, ChevronUp, ArrowRight, Clock, RefreshCw } from 'lucide-react';

const PendingEncomiendas = ({ onResume, lang }) => {
  const [open, setOpen] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDrafts();
  }, []);

  const fetchDrafts = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('encomiendas')
      .select(`
        *,
        customers(full_name, phone_number),
        encomienda_packages(id, recipient_name)
      `)
      .eq('status', 'DRAFT')
      .order('created_at', { ascending: false });
    setDrafts(data || []);
    setLoading(false);
  };

  // Hide entirely while loading first time, or if no drafts
  if (!loading && drafts.length === 0) return null;

  const count = drafts.length;
  const label = lang === 'en'
    ? `${count} pending encomienda${count !== 1 ? 's' : ''}`
    : `${count} encomienda${count !== 1 ? 's' : ''} pendiente${count !== 1 ? 's' : ''}`;

  return (
    <div className="max-w-3xl mx-auto mb-4">
      {/* Trigger */}
      <div
        onClick={() => setOpen(o => !o)}
        className="tile flex items-center gap-3 cursor-pointer hover:bg-gray-50 transition"
        style={{ padding: '10px 16px', background: 'var(--amber-soft)', borderColor: 'var(--amber)' }}
      >
        <span
          className="avatar"
          style={{ width: '24px', height: '24px', background: 'var(--amber)', fontSize: '12px' }}
        >
          {loading ? '…' : count}
        </span>
        <span className="flex-1 font-bold text-sm" style={{ color: 'var(--amber)' }}>{label}</span>
        <button
          onClick={e => { e.stopPropagation(); fetchDrafts(); }}
          className="btn-ghost"
          style={{ padding: '4px', color: 'var(--amber)' }}
          title={lang === 'en' ? 'Refresh' : 'Actualizar'}
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
        {open ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
      </div>

      {/* Panel */}
      {open && (
        <div className="tile mt-2 animate-in slide-in-from-top-2" style={{ padding: 0, overflow: 'hidden' }}>
          {loading ? (
            <p className="empty-state">...</p>
          ) : drafts.length === 0 ? (
            <p className="empty-state">
              {lang === 'en' ? 'No pending drafts.' : 'No hay borradores pendientes.'}
            </p>
          ) : (
            <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
              {drafts.map(d => (
                <div key={d.id} className="row-item hover:bg-gray-50 transition" style={{ padding: '12px 16px' }}>
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <span className="row-id" style={{ width: 'auto', minWidth: '80px' }}>
                      {d.unique_encomienda_id}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="tile-title truncate">{d.customers?.full_name || '—'}</p>
                      <div className="flex gap-3 items-center mt-1">
                        <span className="metric-sub" style={{ marginTop: 0 }}>
                          {d.encomienda_packages?.length || 0}{' '}
                          {lang === 'en' ? 'recipients' : 'destinatarios'}
                        </span>
                        <span className="metric-sub flex items-center gap-1" style={{ marginTop: 0 }}>
                          <Clock size={10} />
                          {new Date(d.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => { onResume(d.id); setOpen(false); }}
                    className="btn btn-ghost"
                    style={{ color: 'var(--avianca-red)', fontWeight: 'bold' }}
                  >
                    {lang === 'en' ? 'Continue' : 'Continuar'} <ArrowRight size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PendingEncomiendas;
